import { useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Clapperboard, Lightbulb, RotateCcw, Trophy, ArrowLeft, Music } from 'lucide-react';
import { linesAndLyricsCards } from '@/game/data/linesAndLyrics';

const GOLD = '#D4AF37';
const BURGUNDY = '#4A0F1E';

function ActorFrame({ clue, revealed, onReveal }) {
  return (
    <button
      type="button"
      onClick={onReveal}
      className="group relative w-full rounded-xl p-[3px] text-left transition-transform duration-300 hover:-translate-y-1"
      style={{ background: `linear-gradient(135deg, ${GOLD}, #8a6a14 45%, ${GOLD})`, boxShadow: '0 12px 30px -12px rgba(0,0,0,0.9)' }}
      aria-label={`${clue.actor} — tap to reveal the movie`}
      data-testid={`ll-actor-${clue.slot}`}
    >
      <div className="rounded-[9px] px-3 py-4 h-full flex flex-col items-center justify-center text-center" style={{ background: 'radial-gradient(circle at 50% 20%, #2a0a12 0%, #140408 70%)', minHeight: 150 }}>
        <Clapperboard className="w-7 h-7 mb-3 opacity-80" style={{ color: GOLD }} />
        <p className="text-sm font-bold tracking-wide" style={{ color: '#F6E7C1', fontFamily: 'Playfair Display' }}>{clue.actor}</p>
        <AnimatePresence initial={false}>
          {revealed ? (
            <motion.p key="movie" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-2 text-xs italic" style={{ color: GOLD }} data-testid={`ll-movie-${clue.slot}`}>
              {clue.movie}
            </motion.p>
          ) : (
            <motion.p key="tap" exit={{ opacity: 0 }} className="mt-2 text-[10px] uppercase tracking-[0.2em] opacity-60 group-hover:opacity-100 transition-opacity" style={{ color: GOLD }}>
              Tap for the movie
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </button>
  );
}

function WordSlots({ clue, value, onChange, status, inputRefs }) {
  const letters = clue.answer.length;
  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) inputRefs.current[i - 1]?.focus();
    if (e.key === 'ArrowLeft' && i > 0) inputRefs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < letters - 1) inputRefs.current[i + 1]?.focus();
  };
  const handleChange = (i, e) => {
    const ch = e.target.value.replace(/[^a-z]/gi, '').slice(-1).toUpperCase();
    const next = value.split('');
    while (next.length < letters) next.push('');
    next[i] = ch;
    onChange(next.join(''));
    if (ch && i < letters - 1) inputRefs.current[i + 1]?.focus();
  };
  const border = status === 'correct' ? '#3fbf7f' : status === 'wrong' ? '#e0564b' : GOLD;
  return (
    <div className="mt-3 text-center">
      <p className="text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: GOLD }}>{clue.label}</p>
      <div className="flex justify-center gap-1.5">
        {Array.from({ length: letters }).map((_, i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            value={value[i] || ''}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKey(i, e)}
            maxLength={2}
            inputMode="text"
            autoCapitalize="characters"
            className="w-8 h-10 sm:w-9 sm:h-11 rounded-md text-center text-lg font-black uppercase outline-none focus:ring-2 transition-colors"
            style={{ background: '#1b0609', color: '#F6E7C1', border: `2px solid ${border}`, '--tw-ring-color': GOLD }}
            data-testid={`ll-input-${clue.slot}-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function LinesAndLyricsPage() {
  const [cardIndex, setCardIndex] = useState(0);
  const card = linesAndLyricsCards[cardIndex];
  const [guesses, setGuesses] = useState(() => card.clues.map(() => ''));
  const [revealed, setRevealed] = useState(() => card.clues.map(() => false));
  const [artistHint, setArtistHint] = useState(false);
  const [checked, setChecked] = useState(false);
  const refs = useRef(card.clues.map(() => ({ current: [] })));

  const statuses = useMemo(
    () => card.clues.map((c, i) => (!checked ? 'idle' : guesses[i].trim() === c.answer ? 'correct' : 'wrong')),
    [card, guesses, checked]
  );
  const solved = checked && statuses.every((s) => s === 'correct');
  const hintsUsed = revealed.filter(Boolean).length + (artistHint ? 1 : 0);
  const score = Math.max(100 - hintsUsed * 15, 25);

  const reset = (nextIndex = cardIndex) => {
    const next = linesAndLyricsCards[nextIndex];
    setCardIndex(nextIndex);
    setGuesses(next.clues.map(() => ''));
    setRevealed(next.clues.map(() => false));
    setArtistHint(false);
    setChecked(false);
    refs.current = next.clues.map(() => ({ current: [] }));
  };

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: `radial-gradient(ellipse at top, ${BURGUNDY} 0%, #150307 45%, #080203 100%)` }} data-testid="lines-and-lyrics-page">
      <Helmet>
        <title>Lines and Lyrics — The Music Gauntlet | MGN1.6 Games</title>
        <meta name="description" content="Name that song. Four actors, four movies, one hidden song title. Play Lines and Lyrics from MGN1.6." />
      </Helmet>

      <div className="max-w-3xl mx-auto">
        <Link to="/games" className="inline-flex items-center gap-2 text-sm mb-6 hover:underline" style={{ color: GOLD }} data-testid="ll-back-to-games">
          <ArrowLeft className="w-4 h-4" /> All games
        </Link>

        {/* Marquee header */}
        <div className="rounded-2xl p-[3px]" style={{ background: `linear-gradient(135deg, ${GOLD}, #7a5a10 50%, ${GOLD})` }}>
          <div className="rounded-[13px] px-6 py-8 text-center relative overflow-hidden" style={{ background: '#1a0509' }}>
            <div className="absolute inset-x-0 top-0 h-1 flex justify-around px-4 pt-2">
              {Array.from({ length: 18 }).map((_, i) => (
                <span key={i} className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: GOLD, animationDelay: `${i * 90}ms` }} />
              ))}
            </div>
            <p className="text-xs tracking-[0.35em] uppercase mb-2" style={{ color: GOLD }}>{card.header.subtitle}</p>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight" style={{ fontFamily: 'Playfair Display', color: '#F6E7C1' }} data-testid="ll-title">
              {card.header.title}
            </h1>
            <p className="mt-4 text-sm tracking-[0.3em] uppercase font-bold" style={{ color: GOLD }}>{card.header.prompt}</p>
            <p className="mt-2 text-xs text-white/60 max-w-md mx-auto">
              Each actor starred in a movie. One word from each title, in order, spells the song. Tap a frame if you need the movie — every hint costs points.
            </p>
          </div>
        </div>

        {/* Actors + word slots */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {card.clues.map((clue, i) => (
            <motion.div key={clue.slot} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
              <ActorFrame
                clue={clue}
                revealed={revealed[i]}
                onReveal={() => setRevealed((r) => r.map((v, idx) => (idx === i ? true : v)))}
              />
              <WordSlots
                clue={clue}
                value={guesses[i]}
                status={statuses[i]}
                inputRefs={refs.current[i]}
                onChange={(val) => { setChecked(false); setGuesses((g) => g.map((v, idx) => (idx === i ? val : v))); }}
              />
            </motion.div>
          ))}
        </div>

        {/* Hint + actions */}
        <div className="mt-8 rounded-2xl border p-5 flex flex-col sm:flex-row sm:items-center gap-4" style={{ borderColor: `${GOLD}55`, background: '#12040780' }}>
          <button
            type="button"
            onClick={() => setArtistHint(true)}
            disabled={artistHint}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold disabled:opacity-60"
            style={{ background: GOLD, color: '#1a0509' }}
            data-testid="ll-hint-btn"
          >
            <Lightbulb className="w-4 h-4" /> HINT
          </button>
          <div className="flex-1 text-sm" style={{ color: '#F6E7C1' }}>
            {artistHint ? (
              <span data-testid="ll-artist-hint">Performed by <strong>{card.artists.join(' & ')}</strong></span>
            ) : (
              <span className="text-white/50">Reveal who performs the song.</span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setChecked(true)}
              className="px-5 py-2 rounded-full text-sm font-bold border-2 transition-colors hover:bg-white/5"
              style={{ borderColor: GOLD, color: GOLD }}
              data-testid="ll-check-btn"
            >
              Check
            </button>
            <button type="button" onClick={() => reset()} className="p-2 rounded-full border-2 hover:bg-white/5" style={{ borderColor: `${GOLD}66`, color: GOLD }} aria-label="Reset card" data-testid="ll-reset-btn">
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        <AnimatePresence>
          {checked && !solved && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-4 text-center text-sm" style={{ color: '#e0564b' }} data-testid="ll-wrong">
              Not yet — the red boxes are off. Try another word from that movie's title.
            </motion.p>
          )}
          {solved && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-6 rounded-2xl p-6 text-center"
              style={{ background: `linear-gradient(135deg, ${GOLD}22, transparent)`, border: `1px solid ${GOLD}` }}
              data-testid="ll-solved"
            >
              <Trophy className="w-10 h-10 mx-auto mb-3" style={{ color: GOLD }} />
              <p className="text-xs tracking-[0.3em] uppercase" style={{ color: GOLD }}>That's the one</p>
              <h2 className="text-3xl font-black mt-1" style={{ fontFamily: 'Playfair Display', color: '#F6E7C1' }}>{card.answer.title}</h2>
              <p className="text-sm mt-1 text-white/70">{card.answer.performedBy.join(' & ')}</p>
              <p className="mt-4 text-sm" style={{ color: GOLD }} data-testid="ll-score">Score: {score} / 100 · {hintsUsed} hint{hintsUsed === 1 ? '' : 's'} used</p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <a
                  href={`https://www.youtube.com/results?search_query=${encodeURIComponent(`${card.answer.title} ${card.answer.performedBy.join(' ')}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold"
                  style={{ background: GOLD, color: '#1a0509' }}
                  data-testid="ll-listen-link"
                >
                  <Music className="w-4 h-4" /> Hear the song
                </a>
                {linesAndLyricsCards.length > 1 && (
                  <button type="button" onClick={() => reset((cardIndex + 1) % linesAndLyricsCards.length)} className="px-4 py-2 rounded-full text-sm font-bold border-2" style={{ borderColor: GOLD, color: GOLD }} data-testid="ll-next-card">
                    Next card
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="mt-10 text-center text-xs text-white/40">Card {cardIndex + 1} of {linesAndLyricsCards.length} · More cards coming to the Gauntlet.</p>
      </div>
    </div>
  );
}
